// ------------------------------------------------------------------------------
// name: useMeta
// author: mudas
// created: 2026-03-20
// ------------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import { useUnmount } from 'ahooks';

/**
 * Meta 标签属性类型
 * 
 * 支持 HTML 规范的所有官方属性：
 * - name: 标准 meta 名称（SEO、浏览器行为等）
 * - property: 协议扩展属性（Open Graph、Twitter Card 等）
 * - httpEquiv: HTTP 响应头等效属性
 * - charset: 字符编码声明
 * - content: 内容值
 * - media: 媒体查询条件
 */
export interface MetaItem {
  /** 标准 meta 名称属性 */
  name?: string;
  /** 协议扩展属性（如 Open Graph 的 og:title） */
  property?: string;
  /** HTTP 响应头等效属性 */
  httpEquiv?: string;
  /** 字符编码（如 'UTF-8'） */
  charset?: string;
  /** 内容值 */
  content?: string;
  /** 媒体查询条件 */
  media?: string;
}

export interface UseMetaOptions {
  /** 组件卸载时是否恢复原始值 */
  restoreOnUnmount?: boolean;
}

export interface UseMetaResult {
  /** 手动应用/更新 meta 标签，可传入自定义 metaList */
  apply: (metaList?: MetaItem[]) => void;
  /** 手动清理/恢复 meta 标签，可传入自定义 metaList */
  cleanup: (metaList?: MetaItem[]) => void;
}

const DEFAULT_OPTIONS: UseMetaOptions = {
  restoreOnUnmount: false,
};

const isBrowser = typeof window !== 'undefined' && window.document;

/**
 * 生成 meta 标签的唯一标识 key
 */
const getMetaKey = (meta: MetaItem): string | null => {
  if (meta.charset) return 'charset';
  if (meta.name) return `name:${meta.name}`;
  if (meta.property) return `property:${meta.property}`;
  if (meta.httpEquiv) return `httpEquiv:${meta.httpEquiv}`;
  return null;
};

/**
 * 生成 meta 标签的 CSS 选择器
 */
const getMetaSelector = (meta: MetaItem): string | null => {
  if (meta.charset) return 'meta[charset]';
  if (meta.name) return `meta[name="${meta.name}"]`;
  if (meta.property) return `meta[property="${meta.property}"]`;
  if (meta.httpEquiv) return `meta[http-equiv="${meta.httpEquiv}"]`;
  return null;
};

/**
 * 为 meta 元素设置属性
 */
const setMetaAttributes = (element: HTMLMetaElement, meta: MetaItem): void => {
  if (meta.name) element.name = meta.name;
  if (meta.property) element.setAttribute('property', meta.property);
  if (meta.httpEquiv) element.setAttribute('http-equiv', meta.httpEquiv);
  if (meta.charset) element.setAttribute('charset', meta.charset);
  if (meta.content !== undefined) element.content = meta.content;
  if (meta.media) element.media = meta.media;
};

/**
 * 动态管理 HTML meta 标签的 Hook
 * 
 * @description
 * 支持所有官方和常见的 meta 标签属性，包括：
 * - SEO 相关：description, keywords, author, robots 等
 * - 视口设置：viewport
 * - Open Graph：og:title, og:description, og:image 等
 * - Twitter Card：twitter:card, twitter:site 等
 * - HTTP 等效头：content-type, X-UA-Compatible, refresh 等
 * - 字符编码：charset
 * 
 * @returns 返回包含 apply 和 cleanup 方法的结果对象
 * - apply: 手动应用/更新 meta 标签
 * - cleanup: 手动清理/恢复 meta 标签
 * 
 * @example
 * // 基础用法
 * useMeta([
 *   { name: 'description', content: '页面描述' },
 *   { name: 'keywords', content: '关键词1,关键词2' },
 *   { name: 'viewport', content: 'width=device-width, initial-scale=1' },
 * ]);
 * 
 * @example
 * // Open Graph 标签
 * useMeta([
 *   { property: 'og:title', content: '分享标题' },
 *   { property: 'og:description', content: '分享描述' },
 *   { property: 'og:image', content: 'https://example.com/image.jpg' },
 *   { property: 'og:url', content: 'https://example.com/page' },
 * ]);
 * 
 * @example
 * // Twitter Card 标签
 * useMeta([
 *   { name: 'twitter:card', content: 'summary_large_image' },
 *   { name: 'twitter:site', content: '@username' },
 *   { name: 'twitter:title', content: '标题' },
 * ]);
 * 
 * @example
 * // HTTP 等效头
 * useMeta([
 *   { httpEquiv: 'X-UA-Compatible', content: 'IE=edge,chrome=1' },
 *   { httpEquiv: 'refresh', content: '30' },
 * ]);
 * 
 * @example
 * // 字符编码
 * useMeta([{ charset: 'UTF-8' }]);
 * 
 * @example
 * // 卸载时恢复原始值
 * useMeta(
 *   [{ name: 'description', content: '临时描述' }],
 *   { restoreOnUnmount: true }
 * );
 * 
 * @example
 * // 手动应用和清理
 * const { apply, cleanup } = useMeta([
 *   { name: 'description', content: '页面描述' },
 * ]);
 * // 使用初始化时的 metaList
 * apply();
 * cleanup();
 * 
 * @example
 * // 动态更新 meta 标签
 * const { apply, cleanup } = useMeta([]);
 * 
 * // 根据条件动态设置不同的 meta
 * apply([
 *   { name: 'description', content: '动态描述' },
 *   { property: 'og:title', content: '动态标题' },
 * ]);
 * 
 * // 清理指定的 meta 标签
 * cleanup([
 *   { name: 'description' },
 *   { property: 'og:title' },
 * ]);
 */
export const useMeta = (
  metaList: MetaItem[],
  options: UseMetaOptions = DEFAULT_OPTIONS,
): UseMetaResult => {
  const originalMetasRef = useRef<Map<string, string>>(new Map());

  /**
   * 应用/更新 meta 标签
   * @param customMetaList 可选的自定义 meta 列表，不传则使用 hook 初始化时的 metaList
   */
  const apply = (customMetaList?: MetaItem[]) => {
    if (!isBrowser) return;

    const targetList = customMetaList || metaList;

    targetList.forEach((meta) => {
      const key = getMetaKey(meta);
      const selector = getMetaSelector(meta);

      if (!key || !selector) return;

      let metaElement = document.querySelector(selector) as HTMLMetaElement | null;

      if (metaElement) {
        // 保存原始值（仅首次）
        if (!originalMetasRef.current.has(key)) {
          originalMetasRef.current.set(key, metaElement.content || '');
        }
        // 更新属性
        setMetaAttributes(metaElement, meta);
      } else {
        // 创建新的 meta 元素
        metaElement = document.createElement('meta');
        setMetaAttributes(metaElement, meta);
        originalMetasRef.current.set(key, '__CREATED__');

        // 插入到最后一个 meta 标签的后面
        const lastMeta = document.head.querySelector('meta:last-of-type');
        if (lastMeta) {
          lastMeta.after(metaElement);
        } else {
          document.head.appendChild(metaElement);
        }
      }
    });
  };

  /**
   * 清理/恢复 meta 标签
   * @param customMetaList 可选的自定义 meta 列表，不传则使用 hook 初始化时的 metaList
   */
  const cleanup = (customMetaList?: MetaItem[]) => {
    if (!isBrowser) return;

    const targetList = customMetaList || metaList;

    targetList.forEach((meta) => {
      const key = getMetaKey(meta);
      const selector = getMetaSelector(meta);

      if (!key || !selector) return;

      const metaElement = document.querySelector(selector) as HTMLMetaElement | null;
      const originalValue = originalMetasRef.current.get(key);

      if (metaElement) {
        if (originalValue === '__CREATED__') {
          // 动态创建的元素，直接删除
          document.head.removeChild(metaElement);
        } else if (originalValue !== undefined) {
          // 恢复原始值
          metaElement.content = originalValue;
        }
      }
    });

    originalMetasRef.current.clear();
  };

  useEffect(() => {
    apply();
  }, [metaList]);

  useUnmount(() => {
    if (options.restoreOnUnmount) {
      cleanup();
    }
  });

  return { apply, cleanup };
};
